import React from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/**
 * Prefix used for filtering ISO-8601 date strings (YYYY-MM). ``null``
 * means „alle Zeiträume".
 */
export function currentMonthPrefix() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonth(prefix, delta) {
  if (!prefix) return currentMonthPrefix();
  const [y, m] = prefix.split("-").map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function isoStartsWithMonth(iso, prefix) {
  if (!prefix) return true;
  if (!iso) return false;
  return String(iso).slice(0, 7) === prefix;
}

/**
 * Compact month + year picker. Left/right arrow to step, click on the
 * label opens dropdowns for jump. Pass ``value`` as ``YYYY-MM`` or ``null``
 * for the "Alle Zeiträume" state, and ``onChange`` to be notified.
 */
export function MonthYearPicker({ value, onChange, testid = "month-year-picker" }) {
  const [openYear, setOpenYear] = React.useState(false);
  const [openMonth, setOpenMonth] = React.useState(false);

  const year = value ? Number(value.split("-")[0]) : new Date().getFullYear();
  const month = value ? Number(value.split("-")[1]) : new Date().getMonth() + 1;

  const now = new Date();
  const thisYear = now.getFullYear();
  const years = [];
  for (let y = thisYear + 1; y >= thisYear - 6; y--) years.push(y);

  const isAll = value === null;

  return (
    <div className="inline-flex items-stretch bg-white border border-[#E6E6E4] rounded-md overflow-hidden text-sm select-none" data-testid={testid}>
      <button
        type="button"
        disabled={isAll}
        onClick={() => onChange(shiftMonth(value, -1))}
        className="px-2 py-1.5 hover:bg-[#F3F3F1] text-[#5C5E60] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Vorheriger Monat"
        data-testid={`${testid}-prev`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-1 border-l border-r border-[#E6E6E4]">
        <button
          type="button"
          onClick={() => { setOpenMonth((v) => !v); setOpenYear(false); }}
          disabled={isAll}
          className="px-2.5 py-1.5 hover:bg-[#F3F3F1] text-[#1A1A1A] font-medium disabled:text-[#5C5E60] transition-colors relative"
          data-testid={`${testid}-month-btn`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-[#5C5E60]" />
            {isAll ? "Alle Zeiträume" : MONTHS_DE[month - 1]}
          </span>
          {openMonth && !isAll && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-[#E6E6E4] rounded-md shadow-lg z-30 py-1 min-w-[140px] max-h-[280px] overflow-auto">
              {MONTHS_DE.map((label, i) => (
                <div key={i}
                  onClick={(e) => { e.stopPropagation(); onChange(`${year}-${String(i + 1).padStart(2, "0")}`); setOpenMonth(false); }}
                  className={`px-3 py-1.5 text-left cursor-pointer hover:bg-[#F3F3F1] ${(i + 1) === month ? "bg-[#E4F0E8] font-semibold text-[#18322B]" : "text-[#1A1A1A]"}`}
                  data-testid={`${testid}-month-${i + 1}`}
                >
                  {label}
                </div>
              ))}
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={() => { setOpenYear((v) => !v); setOpenMonth(false); }}
          disabled={isAll}
          className="px-2 py-1.5 hover:bg-[#F3F3F1] text-[#1A1A1A] font-medium font-mono-tab disabled:text-[#5C5E60] transition-colors relative"
          data-testid={`${testid}-year-btn`}
        >
          {isAll ? "" : year}
          {openYear && !isAll && (
            <div className="absolute top-full right-0 mt-1 bg-white border border-[#E6E6E4] rounded-md shadow-lg z-30 py-1 min-w-[80px]">
              {years.map((y) => (
                <div key={y}
                  onClick={(e) => { e.stopPropagation(); onChange(`${y}-${String(month).padStart(2, "0")}`); setOpenYear(false); }}
                  className={`px-3 py-1.5 text-left cursor-pointer hover:bg-[#F3F3F1] font-mono-tab ${y === year ? "bg-[#E4F0E8] font-semibold text-[#18322B]" : "text-[#1A1A1A]"}`}
                  data-testid={`${testid}-year-${y}`}
                >
                  {y}
                </div>
              ))}
            </div>
          )}
        </button>
      </div>

      <button
        type="button"
        disabled={isAll}
        onClick={() => onChange(shiftMonth(value, +1))}
        className="px-2 py-1.5 hover:bg-[#F3F3F1] text-[#5C5E60] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Nächster Monat"
        data-testid={`${testid}-next`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={() => onChange(isAll ? currentMonthPrefix() : null)}
        className={`px-3 py-1.5 text-xs border-l border-[#E6E6E4] transition-colors ${isAll ? "bg-[#E4F0E8] text-[#18322B] font-semibold" : "text-[#5C5E60] hover:bg-[#F3F3F1]"}`}
        title={isAll ? "Zurück zum aktuellen Monat" : "Alle Zeiträume anzeigen"}
        data-testid={`${testid}-toggle-all`}
      >
        {isAll ? "aktueller Monat" : "alle"}
      </button>
    </div>
  );
}
